import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, MapPinIcon, UsersIcon, FilterIcon, SearchIcon, ClockIcon, DollarSignIcon, CheckIcon, BookIcon, ShoppingBagIcon, LayoutGridIcon, LayoutListIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { format, parseISO, isBefore, isAfter } from 'date-fns';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkMode } from '../../context/DarkModeContext';
// Mock data for vendor event opportunities
const eventOpportunities: EventData[] = [{
  id: '1',
  title: 'Northwest Book Festival',
  description: 'Annual celebration of books featuring over 100 authors and vendors. Great opportunity for networking and direct sales.',
  date: '2023-12-15',
  applicationDeadline: '2023-11-15',
  location: 'Seattle Convention Center, Seattle, WA',
  expectedAttendance: 5000,
  boothOptions: [{
    type: 'Standard Table',
    price: 250,
    description: 'One 6ft table with 2 chairs'
  }, {
    type: 'Premium Corner Booth',
    price: 400,
    description: 'Corner location with 8ft table, 3 chairs'
  }, {
    type: 'Author Co-op Space',
    price: 100,
    description: 'Shared table space with 3 other authors'
  }],
  eventType: 'Book Fair',
  image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  interestedVendors: 32,
  operator: {
    name: 'Sarah Thompson',
    organization: 'Pacific Literary Events',
    contactEmail: 'sarah.t@pacificlit.com',
    phone: '(206) 555-0123'
  }
}, {
  id: '2',
  title: 'Fantasy & Sci-Fi Con',
  description: 'Genre-specific convention focusing on fantasy and science fiction books, games, and media. Perfect for authors in these genres.',
  date: '2024-01-20',
  applicationDeadline: '2023-12-10',
  location: 'Portland Exhibition Hall, Portland, OR',
  expectedAttendance: 3000,
  boothOptions: [{
    type: 'Author Table',
    price: 200,
    description: 'One 6ft table with 2 chairs'
  }, {
    type: 'Publisher Booth',
    price: 500,
    description: '10x10 booth space with tables and chairs'
  }],
  eventType: 'Convention',
  image: 'https://images.unsplash.com/photo-1519408299519-b7a0274f7d67?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  interestedVendors: 45,
  operator: {
    name: 'Michael Roberts',
    organization: 'Genre Conventions LLC',
    contactEmail: 'michael@genrecon.com',
    phone: '(503) 555-0199'
  }
}, {
  id: '3',
  title: "Children's Book Weekend",
  description: "Family-friendly event focused on children's literature with readings, activities, and opportunities for author signings.",
  date: '2024-02-05',
  applicationDeadline: '2024-01-05',
  location: 'City Library Plaza, San Francisco, CA',
  expectedAttendance: 2000,
  boothOptions: [{
    type: 'Author Table',
    price: 150,
    description: 'One table with chair and signing area'
  }, {
    type: 'Publisher Booth',
    price: 350,
    description: 'Dedicated space for multiple titles and activities'
  }],
  eventType: 'Book Fair',
  image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  interestedVendors: 18
}, {
  id: '4',
  title: 'Literary Arts Festival',
  description: 'Prestigious event celebrating literary fiction, poetry, and non-fiction with panel discussions and book signings.',
  date: '2024-03-15',
  applicationDeadline: '2024-01-30',
  location: 'Downtown Arts Center, Chicago, IL',
  expectedAttendance: 4000,
  boothOptions: [{
    type: 'Standard Author Space',
    price: 275,
    description: 'One table with signing area'
  }, {
    type: 'Premium Author Package',
    price: 450,
    description: 'Prime location table plus 30-minute reading slot'
  }, {
    type: 'Small Press Booth',
    price: 600,
    description: 'Dedicated booth for small publishers'
  }],
  eventType: 'Festival',
  image: 'https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  interestedVendors: 27
}, {
  id: '5',
  title: 'Romance Readers Retreat',
  description: 'Specialized event for romance authors and readers with intimate signing sessions and reader interaction opportunities.',
  date: '2024-02-14',
  applicationDeadline: '2024-01-10',
  location: 'Grand Hotel Ballroom, Denver, CO',
  expectedAttendance: 1500,
  boothOptions: [{
    type: 'Author Table',
    price: 225,
    description: 'Table with branded backdrop and signing area'
  }, {
    type: 'Featured Author Package',
    price: 400,
    description: 'Premium table plus participation in panel discussion'
  }],
  eventType: 'Retreat',
  image: 'https://images.unsplash.com/photo-1515169067868-5387ec356754?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  interestedVendors: 39
}, {
  id: '6',
  title: 'Independent Publishers Expo',
  description: 'Focused on independent publishing with workshops, networking, and sales opportunities for indie authors and small presses.',
  date: '2024-04-10',
  applicationDeadline: '2024-02-28',
  location: 'Tech Campus Conference Center, Austin, TX',
  expectedAttendance: 2500,
  boothOptions: [{
    type: 'Indie Author Table',
    price: 175,
    description: 'Shared table space with one other author'
  }, {
    type: 'Solo Author Booth',
    price: 300,
    description: 'Private table with custom signage'
  }, {
    type: 'Small Press Booth',
    price: 550,
    description: '8x8 booth with shelving and display options'
  }],
  eventType: 'Expo',
  image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  interestedVendors: 22
}];
type EventType = 'All' | 'Book Fair' | 'Convention' | 'Festival' | 'Retreat' | 'Expo';
type PriceRange = 'All' | 'Under $200' | '$200-$300' | '$300-$500' | 'Over $500';
type ViewMode = 'list' | 'grid';
interface EventOperator {
  name: string;
  organization: string;
  contactEmail: string;
  phone: string;
}
interface EventData {
  id: string;
  title: string;
  description: string;
  date: string;
  applicationDeadline: string;
  location: string;
  expectedAttendance: number;
  boothOptions: {
    type: string;
    price: number;
    description: string;
  }[];
  eventType: EventType;
  image: string;
  interestedVendors: number;
  operator: EventOperator;
}
const EventOpportunities = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [eventType, setEventType] = useState<EventType>('All');
  const [priceRange, setPriceRange] = useState<PriceRange>('All');
  const [showFilters, setShowFilters] = useState(false);
  const [interestedEvents, setInterestedEvents] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'open' | 'upcoming' | 'closed'>('open');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [boothSelections, setBoothSelections] = useState<{
    [eventId: string]: {
      type: string;
      quantity: number;
    } | null;
  }>({});
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  // Filter events based on status
  const categorizeEvents = (events: EventData[]) => {
    const today = new Date();
    return {
      open: events.filter(event => {
        const deadlineDate = parseISO(event.applicationDeadline);
        return !isBefore(deadlineDate, today);
      }),
      upcoming: events.filter(event => {
        const eventDate = parseISO(event.date);
        const deadlineDate = parseISO(event.applicationDeadline);
        return isBefore(deadlineDate, today) && isAfter(eventDate, today);
      }),
      closed: events.filter(event => {
        const eventDate = parseISO(event.date);
        return isBefore(eventDate, today);
      })
    };
  };
  // Filter events based on search and filters
  const filteredEvents = eventOpportunities.filter(event => {
    // Search filter
    const matchesSearch = searchTerm === '' || event.title.toLowerCase().includes(searchTerm.toLowerCase()) || event.description.toLowerCase().includes(searchTerm.toLowerCase()) || event.location.toLowerCase().includes(searchTerm.toLowerCase());
    // Event type filter
    const matchesEventType = eventType === 'All' || event.eventType === eventType;
    // Date range filter
    const matchesDateRange = (!startDate || isAfter(parseISO(event.date), parseISO(startDate))) && (!endDate || isBefore(parseISO(event.date), parseISO(endDate)));
    // Price range filter
    let matchesPriceRange = true;
    if (priceRange !== 'All') {
      const lowestPrice = Math.min(...event.boothOptions.map(option => option.price));
      if (priceRange === 'Under $200' && lowestPrice >= 200) {
        matchesPriceRange = false;
      } else if (priceRange === '$200-$300' && (lowestPrice < 200 || lowestPrice > 300)) {
        matchesPriceRange = false;
      } else if (priceRange === '$300-$500' && (lowestPrice < 300 || lowestPrice > 500)) {
        matchesPriceRange = false;
      } else if (priceRange === 'Over $500' && lowestPrice <= 500) {
        matchesPriceRange = false;
      }
    }
    return matchesSearch && matchesEventType && matchesPriceRange && matchesDateRange;
  });
  const categorizedEvents = categorizeEvents(filteredEvents);
  const currentEvents = categorizedEvents[activeTab];
  // Handle booth selection change
  const handleBoothSelection = (eventId: string, boothType: string | null) => {
    setBoothSelections(prev => ({
      ...prev,
      [eventId]: boothType ? {
        type: boothType,
        quantity: 1
      } : null
    }));
  };
  // Handle quantity change
  const handleQuantityChange = (eventId: string, quantity: number) => {
    setBoothSelections(prev => ({
      ...prev,
      [eventId]: prev[eventId] ? {
        ...prev[eventId]!,
        quantity
      } : null
    }));
  };
  // Save event to user's list
  const saveEvent = (eventId: string) => {
    // In a real app, this would make an API call
    setInterestedEvents(prev => [...prev, eventId]);
  };
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Calculate days remaining until deadline
  const getDaysRemaining = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  // Toggle interest in an event
  const toggleInterest = (eventId: string) => {
    setInterestedEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  return <div className={`min-h-screen ${dk.page}`} data-id="element-18699" data-mp="mshmrvtnchlp">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-18700" data-mp="rrax5fdmzshn">
        <div className="mb-8" data-id="element-18701" data-mp="rtnfhtnb44v8">
          <div className="mb-4" data-id="element-18702" data-mp="2nufm8od9xwoi">
            <BackButton data-id="element-18703" data-mp="2nte2zwm3u3vp" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-18704" data-mp="2jtdne8n9rmn2">
            Event Opportunities for Authors & Vendors
          </h1>
          <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-18705" data-mp="2xs3khoa39mbf">
            Find and apply for upcoming events where you can showcase your books
            and merchandise
          </p>
        </div>

        {/* Search and Filters */}
        <div className={`rounded-lg shadow-md p-6 mb-8 border ${dk.card}`} data-id="element-18706" data-mp="254qx5bw3tah2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-18707" data-mp="206pe2qyohb5l">
            <div className="relative flex-1" data-id="element-18708" data-mp="2ev2fi3gjbm7h">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-18709" data-mp="2a3kddc2rzfkx" />
              <input type="text" placeholder="Search events by title, description, or location..." className={`pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-18710" data-mp="1md7zlkkumomf" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center font-medium text-sm ${rt.accentLink}`} data-id="element-18711" data-mp="3k2lfzgnago8v">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-18712" data-mp="fnlpc0pyn1nc" />
              Filters {showFilters ? '(Hide)' : '(Show)'}
            </button>
          </div>

          {showFilters && <div className={`border-t pt-4 space-y-4 ${dk.border}`} data-id="element-18713" data-mp="kk2m1jb86oc1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-18714" data-mp="6m5qmq8icnyy">
                <div data-id="element-18715" data-mp="bkpb0m7gdrp7">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18716" data-mp="bk77d4igob79">
                    Event Type
                  </label>
                  <select value={eventType} onChange={e => setEventType(e.target.value as EventType)} className={`w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-18717" data-mp="2wcr4p3imb7qq">
                    <option value="All" data-id="element-18718" data-mp="31wmgwpy6afmi">All Event Types</option>
                    <option value="Book Fair" data-id="element-18719" data-mp="2xuhkjjgcb4w6">Book Fair</option>
                    <option value="Convention" data-id="element-18720" data-mp="2xs4xwszn06cs">Convention</option>
                    <option value="Festival" data-id="element-18721" data-mp="3uj76bkv38hnf">Festival</option>
                    <option value="Retreat" data-id="element-18722" data-mp="3plnhxs4oeohr">Retreat</option>
                    <option value="Expo" data-id="element-18723" data-mp="3pm6xj26ut0sm">Expo</option>
                  </select>
                </div>
                <div data-id="element-18724" data-mp="3lhzarajyhux1">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18725" data-mp="3gkpmvzc7trbb">
                    Price Range
                  </label>
                  <select value={priceRange} onChange={e => setPriceRange(e.target.value as PriceRange)} className={`w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-18726" data-mp="2a2akoabeofs0">
                    <option value="All" data-id="element-18727" data-mp="tny5b5q6kopp">All Price Ranges</option>
                    <option value="Under $200" data-id="element-18728" data-mp="yewm06f2zoth">Under $200</option>
                    <option value="$200-$300" data-id="element-18729" data-mp="yg7o81svooih">$200 - $300</option>
                    <option value="$300-$500" data-id="element-18730" data-mp="13essccb93sc7">$300 - $500</option>
                    <option value="Over $500" data-id="element-18731" data-mp="18d3x48movcnk">Over $500</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-18732" data-mp="1dbxewd90dptj">
                <div data-id="element-18733" data-mp="1hdsgzjyrk78d">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18734" data-mp="1he24n0wc62aw">
                    Start Date
                  </label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={`w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-18735" data-mp="3j4ly4boegwih" />
                </div>
                <div data-id="element-18736" data-mp="zcwohlrwi1jr">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18737" data-mp="z98uymmghf51">
                    End Date
                  </label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={`w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-18738" data-mp="197oloohkmme0" />
                </div>
              </div>
            </div>}
        </div>

        {/* Status Tabs + View Toggle */}
        <div className="flex items-center justify-between mb-4" data-id="element-18739" data-mp="1n0xou7exebo3">
          <div className={`flex border-b ${dk.border}`} data-id="element-18740" data-mp="1n2qzdxsntg1d">
            {[{
            id: 'open',
            label: `Open Applications (${categorizedEvents.open.length})`
          }, {
            id: 'upcoming',
            label: `Upcoming Events (${categorizedEvents.upcoming.length})`
          }, {
            id: 'closed',
            label: `Past Events (${categorizedEvents.closed.length})`
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as typeof activeTab)} className={`px-4 py-2 font-medium text-sm mr-4 transition-colors ${activeTab === tab.id ? `${rt.textPrimary} border-b-2 ${rt.borderAccent}` : `${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-18741" data-mp="1smybhhvisuj6">
                {tab.label}
              </button>)}
          </div>
          <div className="flex items-center space-x-2" data-id="element-18742" data-mp="19wqc9auxyo89">
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? `${rt.textPrimary} ${rt.bgMedium}` : dk.textFaint}`} data-id="element-18743" data-mp="19yjn72cl7um2">
              <LayoutListIcon className="h-5 w-5" data-id="element-18744" data-mp="1fg0qpd4jhrrt" />
            </button>
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? `${rt.textPrimary} ${rt.bgMedium}` : dk.textFaint}`} data-id="element-18745" data-mp="1fidggcgjh6ff">
              <LayoutGridIcon className="h-5 w-5" data-id="element-18746" data-mp="11ovz8ybbs5sk" />
            </button>
          </div>
        </div>

        {/* Events List View */}
        {viewMode === 'list' ? <div className="space-y-6" data-id="element-18747" data-mp="wq2imx0qryo6">
            {currentEvents.map(event => {
          const daysRemaining = getDaysRemaining(event.applicationDeadline);
          const isInterested = interestedEvents.includes(event.id);
          const deadlinePassed = daysRemaining <= 0;
          const selectedBooth = boothSelections[event.id];
          return <div key={event.id} className={`rounded-lg shadow-md overflow-hidden border ${dk.card}`} data-id="element-18748" data-mp="2uclj9ev451nj">
                  <div className="md:flex" data-id="element-18749" data-mp="33ebb2lswq2lu">
                    <div className="md:w-1/3 h-48 md:h-auto relative" data-id="element-18750" data-mp="33el1ubls0dsl">
                      <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-18751" data-mp="38degi7csuauc" />
                      <div className="absolute top-2 right-2 bg-white bg-opacity-90 rounded-full px-3 py-1 text-sm font-medium flex items-center text-gray-800" data-id="element-18752" data-mp="2fnpf3sw6g8s7">
                        <UsersIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-18753" data-mp="2klqykexima47" />
                        {event.expectedAttendance.toLocaleString()} Expected
                      </div>
                      <div className={`absolute bottom-2 left-2 text-white rounded-full px-3 py-1 text-sm font-medium ${rt.bgDark}`} data-id="element-18754" data-mp="1wx7lzn7fnb6b">
                        {event.eventType}
                      </div>
                    </div>
                    <div className="p-6 md:w-2/3" data-id="element-18755" data-mp="1ddhejljtfxc9">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4" data-id="element-18756" data-mp="1ddr4580y82hf">
                        <div data-id="element-18757" data-mp="1ibj7cy71nb0x">
                          <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-18758" data-mp="kokaongcq331">
                            {event.title}
                          </h2>
                          <div className={`flex items-center mt-2 text-sm ${dk.textMuted}`} data-id="element-18759" data-mp="um83v8yb72j3">
                            <CalendarIcon className="h-4 w-4 mr-2" data-id="element-18760" data-mp="zerh24t33vnd" />
                            {formatDate(event.date)}
                          </div>
                          <div className={`flex items-center mt-1 text-sm ${dk.textMuted}`} data-id="element-18761" data-mp="6wm3ii67ah9i">
                            <MapPinIcon className="h-4 w-4 mr-2" data-id="element-18762" data-mp="3vi92t5kad5a9" />
                            {event.location}
                          </div>
                        </div>
                        <div className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${deadlinePassed ? 'bg-red-100 text-red-800' : daysRemaining <= 14 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-18763" data-mp="3ljt8ktpypfwj">
                          <ClockIcon className="h-4 w-4 mr-1" data-id="element-18764" data-mp="37so7dn6y4fk3" />
                          {deadlinePassed ? 'Application Closed' : `${daysRemaining} days to apply`}
                        </div>
                      </div>

                      <p className={`mb-4 ${dk.textSecondary}`} data-id="element-18765" data-mp="2swp4qs2lv7mc">
                        {event.description}
                      </p>

                      {/* Booth Options */}
                      <div className="mb-4" data-id="element-18766" data-mp="2e9qcwvzu6b73">
                        <h3 className={`text-sm font-medium mb-2 flex items-center ${dk.textPrimary}`} data-id="element-18767" data-mp="2e8p30uuk3uqy">
                          <DollarSignIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-18768" data-mp="2591zo3i9oev9" />
                          Booth Options
                        </h3>
                        <div className="space-y-4" data-id="element-18769" data-mp="2xwxuf2o9zwpe">
                          <div className="flex flex-col md:flex-row md:items-end gap-4" data-id="element-18770" data-mp="2xzkbbsx23oka">
                            <div className="flex-grow" data-id="element-18771" data-mp="31y9llcmsspzp">
                              <label htmlFor={`booth-select-${event.id}`} className={`block text-sm mb-1 ${dk.textMuted}`} data-id="element-18772" data-mp="2o862guj7tceh">
                                Select a booth type
                              </label>
                              <select id={`booth-select-${event.id}`} className={`w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} value={selectedBooth?.type || ''} onChange={e => handleBoothSelection(event.id, e.target.value || null)} data-id="element-18773" data-mp="3lnach6d8j9tp">
                                <option value="" data-id="element-18774" data-mp="382z39h8f8ooy">
                                  -- Select booth type --
                                </option>
                                {event.boothOptions.map(option => <option key={option.type} value={option.type} data-id="element-18775" data-mp="3h45mhy2ggdlw">
                                    {option.type} - ${option.price} -{' '}
                                    {option.description}
                                  </option>)}
                              </select>
                            </div>
                            {selectedBooth && <div className="flex items-end gap-2" data-id="element-18776" data-mp="3711uut6dzok2">
                                <div data-id="element-18777" data-mp="3bwqs2puxotd7">
                                  <label htmlFor={`quantity-select-${event.id}`} className={`block text-sm mb-1 ${dk.textMuted}`} data-id="element-18778" data-mp="3fzx0o45iqwmu">
                                    Quantity
                                  </label>
                                  <select id={`quantity-select-${event.id}`} className={`border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} value={selectedBooth.quantity} onChange={e => handleQuantityChange(event.id, parseInt(e.target.value))} data-id="element-18779" data-mp="3uojj4eqg04ia">
                                    {[1, 2, 3, 4, 5].map(num => <option key={num} value={num} data-id="element-18780" data-mp="1j30xr0boj6s1">
                                        {num}
                                      </option>)}
                                  </select>
                                </div>
                                <div className="text-right" data-id="element-18781" data-mp="26yddqz79lnfs">
                                  <div className={`text-xs mb-1 ${dk.textMuted}`} data-id="element-18782" data-mp="220k5m0meijcv">
                                    Total
                                  </div>
                                  <div className={`font-medium ${rt.textPrimary}`} data-id="element-18783" data-mp="2uny4w5dknr05">
                                    $
                                    {(event.boothOptions.find(o => o.type === selectedBooth.type)?.price || 0) * selectedBooth.quantity}
                                  </div>
                                </div>
                              </div>}
                          </div>
                          {selectedBooth && <div className={`border rounded-md p-3 text-sm ${rt.bgLight} ${rt.borderAccent}`} data-id="element-18784" data-mp="2zv4lkrdv1i2x">
                              <div className={`font-medium ${rt.textPrimary}`} data-id="element-18785" data-mp="2pwf4v1xpa3o9">
                                {selectedBooth.type} × {selectedBooth.quantity}
                              </div>
                              <div className={`mt-1 ${dk.textSecondary}`} data-id="element-18786" data-mp="o391vlz6qjvt">
                                {event.boothOptions.find(o => o.type === selectedBooth.type)?.description}
                              </div>
                            </div>}
                        </div>
                      </div>

                      <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t ${dk.border}`} data-id="element-18787" data-mp="2s9so4jnri0bs">
                        <div className="flex items-center gap-4" data-id="element-18788" data-mp="31b0f0gny7yvu">
                          <div className={`flex items-center text-sm ${dk.textMuted}`} data-id="element-18789" data-mp="31a7c5qs41tiz">
                            <BookIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-18790" data-mp="16wfuq53ji5cz" />
                            {event.interestedVendors} vendors interested
                          </div>
                          <div className={`text-sm ${dk.textMuted}`} data-id="element-18791" data-mp="h1yev8ffqxxf">
                            <span className="font-medium" data-id="element-18792" data-mp="l2qpo63yhavt">Apply by:</span>{' '}
                            {formatDate(event.applicationDeadline)}
                          </div>
                        </div>
                        <button onClick={() => toggleInterest(event.id)} disabled={deadlinePassed} className={`px-4 py-2 rounded-md text-sm font-medium flex items-center text-white transition-colors ${deadlinePassed ? 'bg-gray-300 cursor-not-allowed' : isInterested ? 'bg-green-600 hover:bg-green-700' : rt.primaryButton}`} data-id="element-18793" data-mp="usa8jbro54qo">
                          {isInterested ? <>
                              <CheckIcon className="h-4 w-4 mr-2" data-id="element-18794" data-mp="1iieehpsbxgku" />
                              Interest Expressed
                            </> : <>
                              <ShoppingBagIcon className="h-4 w-4 mr-2" data-id="element-18795" data-mp="37zjmb0yh3pek" />
                              Express Interest
                            </>}
                        </button>
                      </div>

                      {event.operator && <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`} data-id="element-18796" data-mp="2tanijg3g8yjr">
                          <h4 className={`text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-18797" data-mp="2kb0e0lffbcmh">
                            Event Operator
                          </h4>
                          <div className="flex flex-wrap gap-x-6 gap-y-2" data-id="element-18798" data-mp="2adcktzxgud6f">
                            {[{
                      label: 'Name',
                      value: event.operator.name
                    }, {
                      label: 'Organization',
                      value: event.operator.organization
                    }, {
                      label: 'Email',
                      value: event.operator.contactEmail
                    }].map(({
                      label,
                      value
                    }) => <div key={label} className="flex items-center" data-id="element-18799" data-mp="hdsqiw7a61qq">
                                <span className={`text-xs mr-1 ${dk.textFaint}`} data-id="element-18800" data-mp="ce6cikjmohgv">
                                  {label}:
                                </span>
                                <p className={`text-sm ${dk.textSecondary}`} data-id="element-18801" data-mp="mb1bf4jtcjvo">
                                  {value}
                                </p>
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>
                </div>;
        })}

            {currentEvents.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-18802" data-mp="1w9u15h87en8f">
                <CalendarIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textFaint}`} data-id="element-18803" data-mp="20cgvbosckk8s" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-18804" data-mp="2o1jnhqklxvhj">
                  No events found
                </h3>
                <p className={dk.textMuted} data-id="element-18805" data-mp="2t1xjga8xpez7">
                  Try adjusting your search or filters
                </p>
              </div>}
          </div> /* Grid View */ : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-18806" data-mp="2j7x7bof8j9gz">
            {currentEvents.map(event => {
          const daysRemaining = getDaysRemaining(event.applicationDeadline);
          const deadlinePassed = daysRemaining <= 0;
          const location = event.location.split(',').map(part => part.trim());
          const city = location[0];
          const country = location[location.length - 1];
          return <div key={event.id} className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100" data-id="element-18807" data-mp="37n1xrd2rhlke">
                  <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-18808" data-mp="32ptfzdlizjjo" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-18809" data-mp="2tvwt1wkje337" />
                  <div className="absolute top-2 right-2 z-10" data-id="element-18810" data-mp="2jw60i0fjdy5r">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full text-white ${rt.bgDark}`} data-id="element-18811" data-mp="2eye9r9qmoi35">
                      {event.eventType}
                    </span>
                  </div>
                  <div className="absolute top-2 left-2 z-10" data-id="element-18812" data-mp="29rzb0vr0kq86">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${deadlinePassed ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`} data-id="element-18813" data-mp="2esn4do2ptj5k">
                      {deadlinePassed ? 'Application Closed' : 'Open Application'}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-18814" data-mp="1w1vxct67a4uc">
                    <h3 className="text-lg font-semibold leading-tight mb-1" data-id="element-18815" data-mp="20tdut790lnak">
                      {event.title}
                    </h3>
                    <div className="flex items-center text-sm" data-id="element-18816" data-mp="13bdspnptvvet">
                      <MapPinIcon className="h-4 w-4 mr-1" data-id="element-18817" data-mp="18aqj2ifwrkm5" />
                      {city}, {country}
                    </div>
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </div>;
};
export default EventOpportunities;